import { AppProps } from 'next/app';
import Script from 'next/script';
import Layout from '~/components/layout';
import { getLang } from '~/utils/translate';
import { initHotjar } from '~/utils/analytics';
import { useEffect } from 'react';
import { AuthProvider } from '~/auth';
import { TranslationProvider } from '~/i18n';
import { DefaultSeo } from 'next-seo';
import config from '~/translation.json';
import { Theme, ThemeProvider } from '~/theme';
import { ReactQueryDevtools } from 'react-query/devtools';
// import { ChatWidget } from '~/components/common/chat-widget';
import { pdfjs } from 'react-pdf';
import { QueryClient, QueryClientProvider } from 'react-query';
import SwiperCore, { Pagination, Navigation, Autoplay } from 'swiper';
import { Toaster } from 'react-hot-toast';
// pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
// import cookie from 'js-cookie';
import 'react-spring-bottom-sheet/dist/style.css';
import '../css/tailwind.css';
import '../css/web.css';
import '../css/theme.css';
import '../css/fonts.css';
import '../css/nprogress.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import ErrorBoundary from '~/components/error/ErrorBoundary';

const { languages } = config;

/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/*                              NEXTJS IS AWESOME                             */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
SwiperCore.use([Pagination, Navigation, Autoplay]);
const App = ({ Component, pageProps, router }: AppProps) => {
   /*
    * Make sure to only use it when necessary as this function will run on every page
    * thats why we are using analytics in here
    **/
   useEffect(() => {
      initHotjar();
   }, []);

   /* ---------------------- Application current language ---------------------- */
   const lang = getLang(router);

   // Manipulating html element for lang and dir attributes
   useEffect(() => {
      const langs = languages as Record<string, string>;
      const dir = langs[lang];
      document.documentElement.lang = lang;
      document.documentElement.dir = dir;
      document.body.dir = dir;
   }, [lang]);

   const translations = require(`~/translations/${lang}`).default;
   const theme = router.asPath?.split('/')[2] as Theme;
   const { hideHeader, hideSubheader, noContainer, AdditionalLayout, showOnboardHeader } =
      Component as any;

   const queryClient = new QueryClient();
   // const ssid_token = cookie.get('ssid_token');

   return (
      <GoogleReCaptchaProvider
         reCaptchaKey={process.env.NEXT_PUBLIC_CAPTCHA_KEY || ''}
         scriptProps={{ defer: true }}>
         <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
               <TranslationProvider lang={lang} translations={translations}>
                  <DefaultSeo
                     titleTemplate="%s | Visit Saudi Partner"
                     defaultTitle="Visit Saudi Partner"
                     description="Visit Saudi Partner"
                  />
                  {process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER ? (
                     <Script
                        strategy="afterInteractive"
                        nonce={process.env.NONCE_VALUE}
                        dangerouslySetInnerHTML={{
                           __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                     new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                     j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                     'https://www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.querySelector('[nonce]');
                     n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));f.parentNode.insertBefore(j,f);
                     })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER}');`,
                        }}
                     />
                  ) : null}
                  {process.env.NEXT_PUBLIC_ADOBE_TAG ? (
                     <Script
                        strategy="afterInteractive"
                        nonce={process.env.NONCE_VALUE}
                        src={`https://assets.adobedtm.com/c68cd5f9e870/c1f9b98f49b1/launch-${process.env.NEXT_PUBLIC_ADOBE_TAG}.min.js`}
                     />
                  ) : null}
                  <ErrorBoundary>
                     <AuthProvider afterLogoutTo={`/${lang}`} afterLoginTo={`/${lang}/dashboard`}>
                        <Toaster
                           position="top-center"
                           reverseOrder={false}
                           containerStyle={{ zIndex: '99999999' }}
                        />
                        <Layout
                           hideHeader={hideHeader}
                           hideSubheader={hideSubheader}
                           noContainer={noContainer}
                           AdditionalLayout={AdditionalLayout}
                           showOnboardHeader={showOnboardHeader}>
                           <Component {...pageProps} />
                           {/* <ChatWidget /> */}
                        </Layout>
                     </AuthProvider>
                  </ErrorBoundary>

                  {/* ------------------------------- App styling ------------------------------ */}
                  {/* this makes sure that main take the full available height of the broweser */}
                  <style jsx global>{`
                     #__next {
                        display: flex;
                        flex-direction: column;
                        min-height: 100vh;
                     }
                     #__next main {
                        flex: 1;
                        background-color: #f8f8f8;
                     }
                  `}</style>
               </TranslationProvider>
            </ThemeProvider>
            <ReactQueryDevtools initialIsOpen />
         </QueryClientProvider>
      </GoogleReCaptchaProvider>
   );
};

export default App;
